import api from './index';
import { fetchReadableStream } from './fetchReadableStream';
import { CorrectedTotalDTO, CreateOSPCorrectedTotalsDTO } from '@callmart/types';

const BASE = '/corrected-totals';

export const uploadCorrectedTotals = async ({
    file,
    setPercentage,
}: {
    file: File;
    setPercentage?: (percentage: number) => void;
}) => {
    const formData = new FormData();
    formData.append('file', file);

    return await fetchReadableStream(`${BASE}/upload`, formData, setPercentage, '\n');
};

export const getCorrectedOrReleasedBiddingCalculatedTotals = async ({
    date,
    location,
    workingUnitLocation,
}: {
    date?: string;
    location?: number;
    workingUnitLocation?: number;
} = {}) => {
    return api.get(BASE, { params: { date, location, workingUnitLocation } }).then((res) => res.data);
};

export const getCorrectedTotals = async ({
    dateFrom,
    dateTo,
    workingUnitLocation,
    released,
}: {
    dateFrom: string;
    dateTo: string;
    workingUnitLocation: number;
    released?: boolean;
}) => {
    return api.get(BASE, { params: { dateFrom, dateTo, workingUnitLocation, released } }).then((res) => res.data);
};

export const getSumOfCorrectedTotals = async ({
    dateFrom,
    dateTo,
    workingUnitLocation,
}: {
    dateFrom: string;
    dateTo: string;
    workingUnitLocation: number;
}) => {
    return api.get(`${BASE}/sum`, { params: { dateFrom, dateTo, workingUnitLocation } }).then((res) => res.data);
};

export const getLastCorrectedTotalsUploadDate = async ({ workingUnitLocation }: { workingUnitLocation: number }) => {
    return api.get(`${BASE}/last-upload-date`, { params: { workingUnitLocation } }).then((res) => res.data);
};

export const releaseCorrectedTotals = async ({
    dateFrom,
    dateTo,
    workingUnitLocation,
}: {
    dateFrom: string;
    dateTo: string;
    workingUnitLocation: number | number[];
}) => {
    return api.post(`${BASE}/release`, { dateFrom, dateTo, workingUnitLocation }).then((res) => res.data);
};

export const getLastCorrectedTotalsReleaseDate = async ({ workingUnitLocation }: { workingUnitLocation: number }) => {
    return api.get(`${BASE}/last-release-date`, { params: { workingUnitLocation } }).then((res) => res.data);
};

export const getCorrectedTotalsForLocation = async ({
    dateFrom,
    dateTo,
    location,
}: {
    dateFrom: string;
    dateTo: string;
    location?: number;
}) => {
    return api.get(`${BASE}/by-location`, { params: { dateFrom, dateTo, location } }).then((res) => res.data);
};

export const upsertOSPCorrectedTotals = async (data: CreateOSPCorrectedTotalsDTO) => {
    return api.post(`${BASE}`, data).then((res) => res.data);
};

export const getCorrectedTotalsSummary = async ({ dateFrom, dateTo }: { dateFrom?: string; dateTo?: string }) => {
    return api.get(`${BASE}/summary`, { params: { dateFrom, dateTo } }).then((res) => res.data);
};

export const changeCorrectedTotals = async (data: CorrectedTotalDTO[], markAsDraft = false) => {
    const url = markAsDraft ? `${BASE}/update?markAsDraft=true` : `${BASE}/update`;
    return api.post(url, data).then((res) => res.data);
};

/**
 * Check for unreleased OSP drafts in a date range.
 * Used by TEF to determine if there are drafts to overwrite.
 */
export const checkOspDrafts = async ({
    workingUnitLocation,
    dateFrom,
    dateTo,
}: {
    workingUnitLocation: number;
    dateFrom: string;
    dateTo: string;
}): Promise<{ hasUnreleasedDrafts: boolean; draftCount: number; draftIds: number[] }> => {
    return api
        .get(`${BASE}/check-osp-drafts`, { params: { workingUnitLocation, dateFrom, dateTo } })
        .then((res) => res.data);
};

/**
 * TEF force-create corrected totals when no OSP data exists.
 */
export const tefForceCreate = async (data: {
    workingUnitLocationId: number;
    totals: { date: string; total: number; offeredReportId?: number }[];
    released: boolean;
    overwriteOspDrafts: boolean;
}) => {
    return api.post(`${BASE}/tef-force-create`, data).then((res) => res.data);
};
